"use client";

import {
	Button,
	type ButtonProps,
	Carousel,
	Container,
	DealCard,
	type DealCardProps,
	type DefaultImageProps,
	FullScreenButton,
	Heading,
	Icon,
	Image,
	type ImageProps,
	InputNumber,
	Link,
	Modal,
	Price,
	type PriceProps,
	Rating,
	type RatingProps,
	Tag,
	Text
} from "@pamperedchef/design-system";
import { className } from "@/app/utilities/className";
import { useState } from "react";

import type { AddItemsToHybrisCartRequest } from "@/infrastructure/hybris";

import { AddtoCart } from "../AddToCart";
import * as styles from "./PDPBannerArea.module.scss";

/**
 * Widen hosted player pages use `/view/video/...` and should load in an iframe.
 * Direct asset URLs under `/content/...` (e.g. `.mp4`) use the HTML5 video element.
 */
function isWidenViewPlayerUrl(url: string): boolean {
	try {
		const u = new URL(url);
		return (
			u.hostname.includes("widen.net") &&
			u.pathname.includes("/view/video/")
		);
	} catch {
		return false;
	}
}

function resolvePosterSrc(src: unknown): string | undefined {
	if (!src) return undefined;
	if (typeof src === "string") return src;
	if (
		typeof src === "object" &&
		"src" in src &&
		typeof (src as { src?: unknown }).src === "string"
	) {
		return (src as { src: string }).src;
	}
	return undefined;
}

type GalleryImageItem<ImageComponentProps = DefaultImageProps> = {
	type: "image";
	media: ImageProps<ImageComponentProps>;
};

type GalleryVideoItem<ImageComponentProps = DefaultImageProps> = {
	type: "video";
	videoSrc: string;
	poster: ImageProps<ImageComponentProps>;
};

export type PDPGalleryItem<ImageComponentProps = DefaultImageProps> =
	| GalleryImageItem<ImageComponentProps>
	| GalleryVideoItem<ImageComponentProps>;

export interface PDPBannerAreaProps<ImageComponentProps = DefaultImageProps> {
	product: {
		info: {
			country: AddItemsToHybrisCartRequest["country"];
			purchaseType: AddItemsToHybrisCartRequest["purchaseType"];
			name: string;
			itemNumber: string;
			rating: RatingProps;
			inventory?: number;
			discontinued?: boolean;
			inventoryLabels?: {
				outOfStock: {
					title: string;
					description: string;
				};
				lowStock: string;
				discontinued: {
					title: string;
					description: string;
				};
			};
			bestSeller?: boolean;
			finalSale?: boolean;
			newThisMonth?: boolean;
			availableWhileSuppliesLast?: boolean;
			urgentLabels?: {
				bestSeller: string;
				finalSale: string;
				newThisMonth: string;
				availableWhileSuppliesLast: string;
			};
			price: PriceProps;
			deals: DealCardProps[];
		};
		cta: ButtonProps;
	};
	gallery: PDPGalleryItem<ImageComponentProps>[];
	dealLabels: {
		singular: string;
		plural: string;
	};
	wishlistLabel?: string;
	registryLabel?: string;
	errorMessage: string | null;
	alreadyAddedMessage: string;
}

interface GalleryModalProps<ImageComponentProps> {
	images: ImageProps<ImageComponentProps>[];
	activeImage: number;
}

/**
 * Gallery modal
 */
function GalleryModal<ImageComponentProps = DefaultImageProps>({
	images,
	activeImage
}: GalleryModalProps<ImageComponentProps>): JSX.Element {
	return (
		<Modal
			className={styles.galleryModal}
			triggerElement={
				<FullScreenButton
					state="collapsed"
					className={styles.expandButton}
				/>
			}
		>
			<Carousel
				asGallery
				slides={images.map((image, index) => (
					// @ts-expect-error - React 19 generic type limitation with Image component
					<Image<ImageComponentProps>
						{...image}
						key={`pdpBannerImage_${index}`}
						aspectRatio="16:9"
						alt={image.alt || ""}
					/>
				))}
				startAt={activeImage}
			/>
		</Modal>
	);
}

interface VideoModalProps<ImageComponentProps> {
	videoSrc: string;
	poster: ImageProps<ImageComponentProps>;
}

function VideoModal<ImageComponentProps = DefaultImageProps>({
	videoSrc,
	poster
}: VideoModalProps<ImageComponentProps>): JSX.Element {
	const [isOpen, setIsOpen] = useState(false);
	const useWidenIframePlayer = isWidenViewPlayerUrl(videoSrc);
	const posterSrc = resolvePosterSrc(poster.src);

	return (
		<>
			<button
				type="button"
				className={styles.videoTrigger}
				aria-label="Play product video"
				aria-expanded={isOpen}
				aria-haspopup="dialog"
				onClick={() => setIsOpen(true)}
			>
				<span className={styles.videoPlayIcon} aria-hidden="true">
					<Icon name="Play" size={24} />
				</span>
			</button>
			<Modal
				className={`${styles.galleryModal} ${styles.videoModal}`}
				isOpen={isOpen}
				setOpen={setIsOpen}
			>
				<div className={styles.videoModalContent}>
					{useWidenIframePlayer ? (
						<iframe
							title="Product video"
							src={videoSrc}
							className={styles.videoIframe}
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
							allowFullScreen
						/>
					) : (
						<video
							controls
							playsInline
							preload="metadata"
							poster={posterSrc}
							className={styles.videoPlayer}
						>
							<source src={videoSrc} />
						</video>
					)}
				</div>
			</Modal>
		</>
	);
}

/**
 * PDPBannerArea Component
 */
export function PDPBannerArea<ImageComponentProps = DefaultImageProps>(
	props: PDPBannerAreaProps<ImageComponentProps>
): JSX.Element {
	const media = props.gallery.slice(0, 5);
	const imageItems = media.filter(
		(item): item is GalleryImageItem<ImageComponentProps> =>
			item.type === "image"
	);
	const imageSlides = imageItems.map((item) => item.media);
	const [numberOfItems, setNumberOfItems] = useState(1);
	const [showAllDeals, setShowAllDeals] = useState(
		props.product.info.deals.length <= 2
	);
	const [activeImage, setActiveImage] = useState(0);

	/**
	 * Handle scroll event on gallery
	 */
	function handleScroll(event: React.UIEvent<HTMLDivElement>): void {
		const target = event.target as HTMLDivElement;
		const scrollLeft = target.scrollLeft;
		const scrollWidth = target.scrollWidth;
		const clientWidth = target.clientWidth;
		const divisor = scrollWidth - clientWidth;
		if (divisor <= 0) return;
		const scrollPercentage = scrollLeft / divisor;
		const imageIndex = Math.round(scrollPercentage * (media.length - 1));

		setActiveImage(imageIndex);
	}

	return (
		<>
			<Container>
				<div className={styles.container}>
					{media.length > 0 && (
						<div className={styles.galleryContainer}>
							<div
								className={styles.gallery}
								onScroll={handleScroll}
							>
								<div
									className={`${styles.imageContainer} ${styles.heroMedia} ${media[0].type === "video" ? styles.videoMedia : ""}`}
								>
									{media[0].type === "video" ? (
										<>
											{
												// @ts-expect-error DS AspectRatioKey is keyof(vertical|horizontal) which collapses to "1:1"; 16:9 is valid for Image
												<Image<ImageComponentProps>
													{...media[0].poster}
													aspectRatio="16:9"
													alt={
														media[0].poster.alt ||
														""
													}
												/>
											}
											<VideoModal<ImageComponentProps>
												videoSrc={media[0].videoSrc}
												poster={media[0].poster}
											/>
										</>
									) : (
										<>
											<Image<ImageComponentProps>
												{...media[0].media}
												alt={media[0].media.alt || ""}
											/>
											{imageSlides.length > 0 && (
												<GalleryModal<ImageComponentProps>
													images={imageSlides}
													activeImage={0}
												/>
											)}
										</>
									)}
								</div>
								{media.length > 1 && (
									<div className={styles.thumbnails}>
										{media
											.slice(1, 5)
											.map((item, index) => (
												<div
													className={`${styles.imageContainer} ${styles.thumbnailMedia} ${item.type === "video" ? styles.videoMedia : ""}`}
													key={index}
												>
													{item.type === "video" ? (
														<>
															{
																// @ts-expect-error DS AspectRatioKey is keyof(vertical|horizontal) which collapses to "1:1"; 16:9 is valid for Image
																<Image<ImageComponentProps>
																	{...item.poster}
																	aspectRatio="16:9"
																	alt={
																		item
																			.poster
																			.alt ||
																		""
																	}
																/>
															}
															<VideoModal<ImageComponentProps>
																videoSrc={
																	item.videoSrc
																}
																poster={
																	item.poster
																}
															/>
														</>
													) : (
														<>
															<Image<ImageComponentProps>
																{...item.media}
																alt={
																	item.media
																		.alt ||
																	""
																}
															/>
															{imageSlides.length >
																0 && (
																<GalleryModal<ImageComponentProps>
																	images={
																		imageSlides
																	}
																	activeImage={Math.min(
																		index +
																			1,
																		imageSlides.length -
																			1
																	)}
																/>
															)}
														</>
													)}
												</div>
											))}
									</div>
								)}
							</div>
							<div className={styles.dots}>
								{media.map((_, index) => (
									<div
										key={index}
										{...className(
											styles.dot,
											activeImage === index
												? styles.dotActive
												: ""
										)}
									/>
								))}
							</div>
						</div>
					)}
					<div className={styles.product}>
						<Heading
							tag="h1"
							styledAs="h1"
							className={`mb-1 ${styles.productTitle}`}
						>
							{props.product.info.name}
						</Heading>
						<Text styledAs="mdr" className="mb-1">
							Item Number: #{props.product.info.itemNumber}
						</Text>
						{props.product.info.discontinued ? (
							<>
								<Text tag="h2" styledAs="smr" className="mb-1">
									{
										props.product.info?.inventoryLabels
											?.discontinued?.title
									}
								</Text>
								<Text styledAs="xsr" className="mb-0">
									{
										props.product.info?.inventoryLabels
											?.discontinued?.description
									}
								</Text>
							</>
						) : (
							<>
								<Rating
									{...props.product.info.rating}
									className="mb-1"
								/>
								<Price
									{...props.product.info.price}
									className={`mb-2 ${styles.price}`}
								/>

								{(props.product.info.inventory || 0) > 0 &&
									(props.product.info.inventory || 0) <
										400 && (
										<Text styledAs="smr" className="mb-1">
											{
												props.product.info
													?.inventoryLabels?.lowStock
											}
										</Text>
									)}

								<div className={styles.urgentLabels}>
									{props.product.info?.bestSeller && (
										<Tag
											color="success"
											kind="subtle"
											size="sm"
											icon={{
												name: "star",
												size: "sm"
											}}
											label={
												props.product.info?.urgentLabels
													?.bestSeller || ""
											}
										/>
									)}
									{props.product.info?.finalSale && (
										<Tag
											color="red"
											kind="subtle"
											size="sm"
											label={
												props.product.info?.urgentLabels
													?.finalSale || ""
											}
										/>
									)}
									{props.product.info?.newThisMonth && (
										<Tag
											color="teal"
											kind="subtle"
											size="sm"
											label={
												props.product.info?.urgentLabels
													?.newThisMonth || ""
											}
										/>
									)}
									{props.product.info
										?.availableWhileSuppliesLast && (
										<Tag
											color="yellow"
											kind="subtle"
											size="sm"
											label={
												props.product.info?.urgentLabels
													?.availableWhileSuppliesLast ||
												""
											}
										/>
									)}
								</div>

								<div className={styles.deals}>
									{props.product.info.deals
										.slice(
											0,
											showAllDeals
												? props.product.info.deals
														.length
												: 2
										)
										.map((deal, index) => (
											<DealCard
												key={index}
												{...deal}
												className={index ? "mt-1" : ""}
											/>
										))}
									{!showAllDeals && (
										/*
										// From React best practices children shouldn't pass as attribute value
										<Button
											kind="tertiary"
											size="lg"
											children={`+${props.product.info.deals.length - 2} ${props.product.info.deals.length > 3 ? props.dealLabels.plural : props.dealLabels.singular}`}
											onClick={() =>
												setShowAllDeals(true)
											}
											className={styles.showAllDeals}
										/>*/

										<Button
											kind="tertiary"
											size="lg"
											onClick={() =>
												setShowAllDeals(true)
											}
											className={styles.showAllDeals}
										>{`+${props.product.info.deals.length - 2} ${props.product.info.deals.length > 3 ? props.dealLabels.plural : props.dealLabels.singular}`}</Button>
									)}
								</div>

								{(props.product.info?.inventory || 1) > 0 ? (
									<div className={styles.ctaSection}>
										<InputNumber
											value={numberOfItems}
											onChange={setNumberOfItems}
											showControls
										/>
										<AddtoCart
											payload={{
												quantity: `${numberOfItems}`,
												productCode:
													props.product.info
														.itemNumber,
												purchaseType:
													props.product.info
														.purchaseType
											}}
											buttonProps={{
												kind: "primary",
												size: "lg",
												...props.product.cta,
												className: styles.cta
											}}
											alreadyAddedMessage={
												props.alreadyAddedMessage
											}
											errorMessage={props.errorMessage}
										/>
										{(props.wishlistLabel ||
											props.registryLabel) && (
											<div
												className={
													styles.secondaryActions
												}
											>
												{props.wishlistLabel && (
													<Link
														kind="tertiary"
														size="sm"
														href="/wishlist"
													>
														<Icon
															name="heart"
															size="sm"
														/>
														{props.wishlistLabel}
													</Link>
												)}
												{props.registryLabel && (
													<Link
														kind="tertiary"
														size="sm"
														href="/registry"
													>
														<Icon
															name="gift"
															size="sm"
														/>
														{props.registryLabel}
													</Link>
												)}
											</div>
										)}
									</div>
								) : (
									<>
										<Text styledAs="smr" className="mb-1">
											{
												props.product.info
													?.inventoryLabels
													?.outOfStock.title
											}
										</Text>
										<Text styledAs="xsr" className="mb-0">
											{
												props.product.info
													?.inventoryLabels
													?.outOfStock.description
											}
										</Text>
									</>
								)}
							</>
						)}
					</div>
				</div>
			</Container>
		</>
	);
}
